import { AccountInfo } from "@azure/msal-browser";
import { Client } from "@microsoft/microsoft-graph-client";
import { loginRequest } from "./authConfig";
import { authService } from "./authService";

interface CustomError extends Error {
  code?: string;
}

/** Ce que Graph renvoie après un PUT de contenu : la preuve de l'écriture. */
export interface UploadedItem {
  id: string;
  name: string;
  size: number;
}

class OneDriveService {
  /**
   * Creates a Graph client for a specific account
   */
  private async getClient(account: AccountInfo): Promise<Client> {
    return Client.init({
      authProvider: async (done) => {
        try {
          const token = await authService.getAccessToken(
            account,
            loginRequest.scopes
          );
          done(null, token);
        } catch (err) {
          done(err as Error, null);
        }
      },
    });
  }

  /**
   * Gets or creates a folder under the app root, given its path relative to it
   * (`YYYY-MM-DD` ou `YYYY-MM-DD/HHMMSS`, le chemin d'un produit).
   * Note: Caching is now handled by React Query via useOneDriveFolder hook.
   */
  async getOrCreateFolder(
    account: AccountInfo,
    path: string
  ): Promise<string> {
    const client = await this.getClient(account);

    try {
      // Try to get the folder first
      const folder = await client
        .api(`/me/drive/special/approot:/${path}`)
        .get();
      return folder.id;
    } catch (err: unknown) {
      // If not found (404), create it
      if (
        err &&
        typeof err === "object" &&
        "statusCode" in err &&
        err.statusCode === 404
      ) {
        // `POST /me/drive/special/approot/children` renvoyait 400 invalidRequest :
        // Graph ne documente la création d'un enfant que sous
        // `/me/drive/items/{parent-id}/children`, jamais sous un segment
        // `special/`. On résout donc le dossier parent — en le créant lui-même
        // au besoin, un dossier de produit vit sous son dossier de date — puis
        // on crée l'enfant par son identifiant. L'annotation conflictBehavior part
        // avec : le GET ci-dessus a déjà tranché l'existence du dossier.
        const separator = path.lastIndexOf("/");
        const parentId =
          separator === -1
            ? (await client.api("/me/drive/special/approot").get()).id
            : await this.getOrCreateFolder(account, path.slice(0, separator));
        const newFolder = await client
          .api(`/me/drive/items/${parentId}/children`)
          .post({ name: path.slice(separator + 1), folder: {} });
        return newFolder.id;
      }
      throw err;
    }
  }

  /**
   * Envoie une photo dans un dossier de date, et rend le DriveItem écrit.
   * Rien n'est rendu tant que la taille distante ne correspond pas aux octets
   * envoyés : l'appelant ne peut donc pas supprimer une photo sur un envoi
   * qui n'a rien écrit.
   */
  async uploadPhoto(
    account: AccountInfo,
    folderId: string,
    filename: string,
    blob: Blob,
    forceRefresh = false
  ): Promise<UploadedItem> {
    const client = await this.getClient(account);

    let item: UploadedItem;
    try {
      // Sans en-tête explicite, `put()` du client Graph impose
      // `Content-Type: application/json` : les octets JPEG partiraient
      // étiquetés JSON, et le service répondrait 400 invalidRequest.
      //
      // `conflictBehavior` vaut `replace` par défaut sur un PUT : deux photos
      // de même nom — le compteur de séquence repart à 1 à chaque ouverture —
      // s'écraseraient en silence. On demande à renommer (cf. #6). L'annotation
      // se met dans l'URL, jamais dans le corps.
      item = await client
        .api(
          `/me/drive/items/${folderId}:/${filename}:/content?@microsoft.graph.conflictBehavior=rename`
        )
        .header("Content-Type", blob.type || "image/jpeg")
        .put(blob);
    } catch (err: unknown) {
      // Handle 401 Unauthorized - retry once with forced token refresh
      if (
        err &&
        typeof err === "object" &&
        "statusCode" in err &&
        err.statusCode === 401 &&
        !forceRefresh
      ) {
        return this.uploadPhoto(account, folderId, filename, blob, true);
      }

      // Handle 403 Quota Exceeded
      if (
        err &&
        typeof err === "object" &&
        "statusCode" in err &&
        "code" in err &&
        err.statusCode === 403 &&
        err.code === "quotaExceeded"
      ) {
        const error: CustomError = new Error(
          "Espace de stockage OneDrive insuffisant."
        );
        error.code = "STORAGE_FULL";
        throw error;
      }

      // Handle 404 Parent Folder Missing (e.g. if user deleted it manually)
      if (
        err &&
        typeof err === "object" &&
        "statusCode" in err &&
        err.statusCode === 404
      ) {
        const error: CustomError = new Error("Dossier parent introuvable.");
        error.code = "FOLDER_MISSING";
        throw error;
      }

      throw err;
    }

    // La réponse du PUT est le DriveItem écrit : c'est la seule preuve que
    // l'objet existe côté OneDrive et qu'il porte bien tous les octets. Un
    // GET de vérification, lui, passerait par le cache du service worker.
    if (item?.size !== blob.size) {
      const error: CustomError = new Error(
        `Envoi non vérifié : ${item?.size ?? "aucun"} octet(s) sur OneDrive pour ${blob.size} envoyés.`
      );
      error.code = "UNVERIFIED_UPLOAD";
      throw error;
    }
    return item;
  }

  /**
   * Gets the web URL of the app's root folder in OneDrive.
   */
  async getAppFolderUrl(account: AccountInfo): Promise<string> {
    const client = await this.getClient(account);
    try {
      const folder = await client.api("/me/drive/special/approot").get();
      return folder.webUrl;
    } catch (err: unknown) {
      console.error("Failed to get app folder URL:", err);
      throw new Error("Impossible de récupérer le lien OneDrive.");
    }
  }
}

export const oneDriveService = new OneDriveService();
